import _dayjs from 'src/Helpers/dayjs'
import { formatDate } from 'src/Constants/formatDate'
import { ArtistBalanceItemProps } from 'src/Components/ArtistBalanceItem'
import GiftIcon from 'src/Assets/Svg/artist-balance-icons/Gift.svg?react'
import { TransactionTypeEnum } from 'src/Types/payments'
import InboxInIcon from 'src/Assets/Svg/artist-balance-icons/Inbox-in.svg?react'
import CardIcon from 'src/Assets/Svg/artist-balance-icons/Card.svg?react'
import InboxOutIcon from 'src/Assets/Svg/artist-balance-icons/inbox-out.svg?react'
import { capitalizeFirstLetter } from 'src/Helpers/layoutHelpers'
import { getValueOrEmptyString, numberToMoney } from 'src/Constants/functions'

import { ArtistBalanceItem } from '.'

const POSITIVE_TRANSACTION_TYPES = [
  TransactionTypeEnum.SUBSCRIPTION,
  TransactionTypeEnum.RECEIVED,
  TransactionTypeEnum.CASHBACK,
  TransactionTypeEnum.INVITED_BONUS,
  TransactionTypeEnum.BONUS,
  TransactionTypeEnum.QUICK_CHECKOUT_BONUS_FUTURE,
  TransactionTypeEnum.QUICK_CHECKOUT_BONUS_CURRENT,
  TransactionTypeEnum.REFERRAL_BONUS,
  TransactionTypeEnum.COUPON_BONUS,
]

export class ArtistBalanceHistoryHelper {
  static formatItemToProps = (
    item: ArtistBalanceItem,
  ): ArtistBalanceItemProps => ({
    id: item.id,
    date: getValueOrEmptyString(item.createdAt),
    amount: this.getAmount(item),
    positive: this.getIsPositive(item),
    text: this.getText(item),
    subText: this.getSubText(item),
    bold: this.getIsBold(item),
    boldAmount: this.getIsBoldAmount(item),
    icon: this.getIcon(item),
  })
  static formatDate1 = (date?: string): string =>
    _dayjs(date).format(formatDate.formatDate1)

  static calculateGroupedAmount = (items: ArtistBalanceItem[]): string => {
    const amount = items.reduce((acc, item) => {
      if (item.transactionType === TransactionTypeEnum.CASHBACK) {
        return acc
      }
      return acc + Number(item.amount)
    }, 0)
    return `$${numberToMoney(amount)}`
  }
  static getAmount = (item: ArtistBalanceItem): string => {
    if (POSITIVE_TRANSACTION_TYPES.includes(item.transactionType)) {
      return `+ $${numberToMoney(item.amount)}`
    }
    if (item.transactionType === TransactionTypeEnum.USED) {
      return `- $${numberToMoney(item.amount)}`
    }
    return `$${numberToMoney(item.amount)}`
  }

  static getIsBoldAmount = (item: ArtistBalanceItem): boolean =>
    item.transactionType === TransactionTypeEnum.CHARGEBACK
      ? false
      : this.getIsBold(item)

  static getIsPositive = (item: ArtistBalanceItem): boolean =>
    POSITIVE_TRANSACTION_TYPES.includes(item.transactionType)

  static getSubText = (item: ArtistBalanceItem): string | null =>
    item.transactionType === TransactionTypeEnum.CHARGEBACK
      ? 'This is due to a payment dispute opened with your payment provider'
      : null

  static getIsBold = (item: ArtistBalanceItem): boolean =>
    item.transactionType === TransactionTypeEnum.CHARGEBACK

  static getText = (item: ArtistBalanceItem): string => {
    const MAP_TEXT_TRANSACTION_TYPE: Record<TransactionTypeEnum, string> = {
      [TransactionTypeEnum.SUBSCRIPTION]: 'Deposited to balance',
      [TransactionTypeEnum.RECEIVED]: `Unused balance from "${item.track.trackName}" ${capitalizeFirstLetter(item.platformType)} campaign`,
      [TransactionTypeEnum.CASHBACK]: 'Cashback added to your balance',
      [TransactionTypeEnum.PAYMENT]: 'Paid from personal card',
      [TransactionTypeEnum.USED]: 'Paid from your balance',
      [TransactionTypeEnum.INVITED_BONUS]: `Welcome bonus (invited by ${item.invitedData?.firstName})`,
      [TransactionTypeEnum.BONUS]: `Referral bonus (${item.invitedData?.firstName ? item.invitedData.firstName + ' joined using your referral link' : 'Artist joined using your referral link'})`,
      [TransactionTypeEnum.REFERRAL_BONUS]: `Referral bonus (${item.referralData?.firstName ? item.referralData.firstName + ' joined using your referral link' : 'Artist joined using your referral link'})`,
      [TransactionTypeEnum.CHARGEBACK]: `Funds for "${item.track.trackName}" ${capitalizeFirstLetter(item.platformType)} campaign are temporarily on hold`,
      [TransactionTypeEnum.QUICK_CHECKOUT_BONUS_FUTURE]: 'Fast payment bonus',
      [TransactionTypeEnum.QUICK_CHECKOUT_BONUS_CURRENT]: 'Fast payment bonus',
      [TransactionTypeEnum.SUBSCRIPTION_PAYMENT_FAILED]:
        'Subscription payment failed',
      [TransactionTypeEnum.COUPON_BONUS]: 'Special bonus from SoundCampaign',
    }
    return MAP_TEXT_TRANSACTION_TYPE[item.transactionType] || ''
  }

  static getIcon = (
    item: ArtistBalanceItem,
  ): ArtistBalanceItemProps['icon'] => {
    const INBOX_TRANSACTION_TYPES = [
      TransactionTypeEnum.SUBSCRIPTION,
      TransactionTypeEnum.RECEIVED,
      TransactionTypeEnum.CASHBACK,
    ]
    if (INBOX_TRANSACTION_TYPES.includes(item.transactionType)) {
      return InboxInIcon
    }
    if (
      item.transactionType === TransactionTypeEnum.PAYMENT ||
      item.transactionType === TransactionTypeEnum.CHARGEBACK
    ) {
      return CardIcon
    }
    if (item.transactionType === TransactionTypeEnum.USED) {
      return InboxOutIcon
    }
    return GiftIcon
  }

  static groupedByCampaignId = (
    items: ArtistBalanceItem[],
  ): ArtistBalanceItem[][] => {
    const map = items.reduce(
      (acc, item) => {
        const { campaignId } = item
        if (!acc[String(campaignId)]) {
          acc[String(campaignId)] = []
        }
        acc[String(campaignId)].push(item)
        return acc
      },
      {} as { [key: string]: ArtistBalanceItem[] },
    )
    return Object.values(map)
  }
}
